import { MOCKUP_MODEL_LIBRARY, type MockupModelCategoryId } from '@/config/mockupModelLibrary'

/**
 * 与左侧「Mockup Type」、Pinia `mockupCategory` 一致（由素材库配置驱动）。
 */
export type MockupCategoryId = MockupModelCategoryId

/**
 * 设备模板：左侧库、时间轴图层、Three 视口共用。
 */
export interface MockupDeviceTemplate {
  id: string
  name: string
  width: number
  height: number
  category: MockupCategoryId
  /** 有值时视口加载 glTF；否则使用占位几何 */
  modelUrl: string | null
  /** 左侧卡片缩略图；无则 CSS 占位 */
  thumbUrl: string | null
  /**
   * glTF 中屏幕网格的 `Object3D.name`；与 {@link MOCKUP_MODEL_LIBRARY} 一致。
   * `null` 时视口使用命名启发式 + 面积回退。
   */
  screenObjName: string | null
}

/** `import.meta.glob` 返回的 key 中用于截取「相对 models 目录」路径的锚点 */
const MODELS_GLOB_PATH_MARKER = '/assets/models/'

/**
 * 构建期由 Vite 收集的 `src/assets/models` 下 glb/png → 打包 URL。
 * 比动态 `new URL(\`../assets/models/${path}\`, import.meta.url)` 更利于静态分析与工具链兼容。
 */
const MOCKUP_MODEL_ASSET_URL_BY_RELATIVE_PATH: Readonly<Record<string, string>> = Object.freeze(
  Object.fromEntries(
    Object.entries(
      import.meta.glob('../assets/models/**/*.{glb,png}', {
        eager: true,
        import: 'default',
        query: '?url',
      }) as Record<string, string>,
    ).map(([key, href]) => {
      const normalized = key.replace(/\\/g, '/')
      const i = normalized.indexOf(MODELS_GLOB_PATH_MARKER)
      const rel = i === -1 ? normalized : normalized.slice(i + MODELS_GLOB_PATH_MARKER.length)
      return [rel, href] as const
    }),
  ),
)

/**
 * 将 `src/assets/models` 下相对路径解析为模块 URL（Vite 构建期处理）。
 *
 * @param relativePath - 相对 `src/assets/models/`
 * @throws 若路径未在素材目录中匹配到文件
 */
function resolveModelAssetUrl(relativePath: string): string {
  const rel = relativePath.replace(/\\/g, '/')
  const url = MOCKUP_MODEL_ASSET_URL_BY_RELATIVE_PATH[rel]
  if (url === undefined) {
    throw new Error(`Unknown mockup model asset: ${relativePath}`)
  }
  return url
}

/**
 * 由 {@link MOCKUP_MODEL_LIBRARY} 生成的运行时模板表（含解析后的资源 URL）。
 */
export const MOCKUP_DEVICE_TEMPLATES: readonly MockupDeviceTemplate[] = MOCKUP_MODEL_LIBRARY.map(
  (e) => ({
    id: e.id,
    name: e.name,
    width: e.width,
    height: e.height,
    category: e.category,
    modelUrl: e.modelRelativePath ? resolveModelAssetUrl(e.modelRelativePath) : null,
    thumbUrl: e.thumbRelativePath ? resolveModelAssetUrl(e.thumbRelativePath) : null,
    screenObjName: e.screenObjName,
  }),
)

export const RESOLUTION_PRESETS = ['1080P', '2K', '4K'] as const
export type ResolutionPresetId = (typeof RESOLUTION_PRESETS)[number]

export const RATIO_PRESETS = ['1:1', '3:4', '4:3', '16:9', '9:16', '4:5', '21:9'] as const
export type RatioPresetId = (typeof RATIO_PRESETS)[number]

/**
 * 各比例预设对应的 **预览 / 编辑画布** 逻辑像素（中间视口与 `sceneWidth`/`sceneHeight` 一致）。
 * 导出像素由 {@link getExportDimensions} 将本表与 {@link EXPORT_RESOLUTION_MULTIPLIER} 组合得到。
 */
export const RATIO_PRESET_DIMENSIONS: Readonly<Record<RatioPresetId, readonly [number, number]>> = {
  '1:1': [1080, 1080],
  '3:4': [1080, 1440],
  '4:3': [1440, 1080],
  '16:9': [1920, 1080],
  '9:16': [1080, 1920],
  '4:5': [1080, 1350],
  '21:9': [2520, 1080],
} as const

/**
 * 相对「1080P 档」预览尺寸的等比放大系数（与 `RATIO_PRESET_DIMENSIONS` 组合为导出像素）。
 * - 2K：按 1:1 目标边长 2048、预览边长 1080 得 `2048/1080`，其余比例同倍率缩放。
 * - 4K：按 16:9 常见 3840×2160 相对 1920×1080 得倍率 `2`。
 */
export const EXPORT_RESOLUTION_MULTIPLIER: Readonly<Record<ResolutionPresetId, number>> = {
  '1080P': 1,
  '2K': 2048 / 1080,
  '4K': 2,
} as const

/**
 * 导出视频像素：分辨率档 × 比例预设（不改变中间预览画布尺寸）。
 *
 * @param resolution - 1080P / 2K / 4K 导出档位
 * @param ratio - 画面比例
 */
export function getExportDimensions(
  resolution: ResolutionPresetId,
  ratio: RatioPresetId,
): readonly [number, number] {
  const [bw, bh] = RATIO_PRESET_DIMENSIONS[ratio]
  const k = EXPORT_RESOLUTION_MULTIPLIER[resolution]
  return [Math.max(1, Math.round(bw * k)), Math.max(1, Math.round(bh * k))] as const
}

/**
 * 未命中比例预设（用户手改预览宽高）时，在保持宽高比前提下按导出档位缩放预览像素。
 *
 * @param resolution - 导出档位
 * @param previewW - 当前预览宽
 * @param previewH - 当前预览高
 */
export function scalePreviewToExportDimensions(
  resolution: ResolutionPresetId,
  previewW: number,
  previewH: number,
): readonly [number, number] {
  const w = Number(previewW)
  const h = Number(previewH)
  if (!Number.isFinite(w) || !Number.isFinite(h) || w < 1 || h < 1) {
    return [1920, 1080] as const
  }
  const k = EXPORT_RESOLUTION_MULTIPLIER[resolution]
  return [Math.max(1, Math.round(w * k)), Math.max(1, Math.round(h * k))] as const
}

/**
 * @param raw - JSON 或表单中的字符串
 * @returns 合法则返回对应 id，否则 `1080P`
 */
export function parseResolutionPresetId(raw: unknown): ResolutionPresetId {
  const s = typeof raw === 'string' ? raw : ''
  return (RESOLUTION_PRESETS as readonly string[]).includes(s) ? (s as ResolutionPresetId) : '1080P'
}

export const TIMELINE_MAX_SECONDS = 10

/** 背景色快捷预设（含浅深中性色）。 */
export const BG_COLOR_PRESETS = [
  '#ffffff',
  '#f4f4f5',
  '#e5e7eb',
  '#cbd5e1',
  '#1a1d26',
  '#0b0d12',
  '#111827',
] as const
